import * as React from "react";
import { useAuth } from "@clerk/clerk-react";

import { getPushPublicKey, registerPushSubscription } from "@/features/push/services/push-api";
import {
  getNotificationPermission,
  isPushSupported,
  resolveDeviceLabel,
  urlBase64ToUint8Array,
} from "@/features/push/lib/push-utils";

async function resolveVapidPublicKey(): Promise<string | null> {
  const fromEnv = import.meta.env.VITE_VAPID_PUBLIC_KEY as string | undefined;
  if (fromEnv && fromEnv.trim().length > 0) return fromEnv.trim();
  try {
    return await getPushPublicKey();
  } catch {
    return null;
  }
}

async function registerCurrentSubscription(subscription: PushSubscription): Promise<void> {
  const json = subscription.toJSON();
  if (!json.endpoint || !json.keys?.p256dh || !json.keys?.auth) return;
  await registerPushSubscription({
    endpoint: json.endpoint,
    keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
    deviceLabel: resolveDeviceLabel(),
  });
}

/**
 * Self-heal the server's push record for THIS device on every authenticated load.
 *
 * Push endpoints rotate, and the Clerk session-end webhook can deactivate the server row while the
 * browser still holds a live subscription — either way the device silently stops receiving push. This
 * re-registers the browser's CURRENT subscription (idempotent server-side, keyed by endpoint) so the
 * server's record always tracks the live endpoint.
 *
 * Strictly opt-in preserving: runs only when permission is already "granted" (it never prompts), and only
 * registers an existing subscription — or silently re-subscribes when permission is held but the sub was
 * lost/rotated away. Does nothing for users who never enabled push, and never runs while signed out.
 */
export function usePushReconcile(): void {
  const { isLoaded, isSignedIn } = useAuth();
  const hasRunRef = React.useRef(false);

  React.useEffect(() => {
    if (!isSignedIn) {
      // Allow a fresh reconcile after the next sign-in.
      hasRunRef.current = false;
      return;
    }
    if (!isLoaded || hasRunRef.current) return;
    if (!isPushSupported() || getNotificationPermission() !== "granted") return;

    hasRunRef.current = true;
    void (async () => {
      try {
        const registration = await navigator.serviceWorker.getRegistration();
        if (!registration) return;

        let subscription = await registration.pushManager.getSubscription();
        if (!subscription) {
          // Permission granted but no live subscription (lost / rotated away) → re-subscribe silently.
          const publicKey = await resolveVapidPublicKey();
          if (!publicKey) return;
          subscription = await registration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToUint8Array(publicKey) as BufferSource,
          });
        }

        await registerCurrentSubscription(subscription);
      } catch {
        // Best-effort self-heal — never disrupt the app (e.g. a 409 if the endpoint is owned by another user).
      }
    })();
  }, [isLoaded, isSignedIn]);
}

/** Headless: mount once inside the authenticated app to keep this device's push record self-healing. */
export function PushSubscriptionReconciler(): null {
  usePushReconcile();
  return null;
}
